import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useSip } from "@/contexts/SipContext";
import {
  Hash,
  Phone,
  ChevronRight,
  Search,
  Loader2,
  Settings,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { Link } from "react-router-dom";

import { API_URL } from "@/utils/api";
const API = API_URL;

export function StarCodesPanel({ onDial }) {
  const { token, user } = useAuth();
  const { makeCall, callStatus } = useSip();
  
  const [starCodes, setStarCodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [inputDialogOpen, setInputDialogOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [expandedCategories, setExpandedCategories] = useState([]);
  
  const isAdmin = user?.role === "customer_admin" || user?.role === "developer";
  
  useEffect(() => {
    fetchStarCodes();
  }, [token]);
  
  const fetchStarCodes = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/star-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStarCodes(response.data.codes || []);
      setCategories(response.data.categories || []);
      
      // Auto-expand first category if present
      if (response.data.categories?.length > 0) {
        setExpandedCategories([response.data.categories[0]]);
      }
    } catch (error) {
      console.error("Failed to fetch star codes:", error);
      // If no codes exist, try to initialize
      if (error.response?.status === 404 || (error.response?.data?.codes || []).length === 0) {
        await initializeStarCodes();
      }
    } finally {
      setLoading(false);
    }
  };
  
  const initializeStarCodes = async () => {
    try {
      const response = await axios.post(`${API}/star-codes/initialize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStarCodes(response.data.codes || []);
      
      // Get categories from codes
      const cats = [...new Set(response.data.codes.map(c => c.category))];
      setCategories(cats);
      
      if (cats.length > 0) {
        setExpandedCategories([cats[0]]);
      }
      
      toast.success("Star codes initialized with defaults");
    } catch (error) {
      console.error("Failed to initialize star codes:", error);
    }
  };
  
  const handleCodeClick = (code) => {
    if (callStatus !== "idle") {
      toast.error("Cannot dial star codes during an active call");
      return;
    }
    
    if (code.requires_input) {
      setSelectedCode(code);
      setInputValue("");
      setInputDialogOpen(true);
    } else {
      dialCode(code.code);
    }
  };
  
  const dialCode = (codeString) => {
    if (onDial) {
      onDial(codeString);
    } else {
      makeCall(codeString);
    }
    toast.info(`Dialing ${codeString}`);
  };
  
  const handleInputSubmit = () => {
    if (!selectedCode || !inputValue.trim()) {
      toast.error("Please enter a value");
      return;
    }
    
    const fullCode = `${selectedCode.code}${inputValue.trim()}`;
    setInputDialogOpen(false);
    dialCode(fullCode);
  };
  
  // Filter codes based on search
  const filteredCodes = searchQuery
    ? starCodes.filter(code =>
        code.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        code.code.includes(searchQuery) ||
        (code.description && code.description.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : starCodes;
  
  // Group codes by category
  const codesByCategory = categories.reduce((acc, cat) => {
    acc[cat] = filteredCodes.filter(c => c.category === cat);
    return acc;
  }, {});
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (starCodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-center p-4">
        <Hash className="w-10 h-10 text-muted-foreground mb-3" />
        <p className="text-muted-foreground text-sm mb-3">
          No star codes configured
        </p>
        {isAdmin && (
          <Button
            size="sm"
            onClick={initializeStarCodes}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Load Defaults
          </Button>
        )}
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="px-4 pb-3 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search star codes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-background/50"
            data-testid="star-code-search"
          />
        </div>
      </div>
      
      {/* Admin Link */}
      {isAdmin && (
        <div className="px-4 pb-2 flex-shrink-0">
          <Link to="/app/company?tab=starcodes">
            <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground hover:text-foreground">
              <Settings className="w-4 h-4 mr-2" />
              Manage Star Codes
            </Button>
          </Link>
        </div>
      )}
      
      {/* Categories and Codes */}
      <div className="flex-1 overflow-y-auto px-2">
        {searchQuery ? (
          // Flat list when searching
          <div className="space-y-1 p-2">
            {filteredCodes.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No codes match "{searchQuery}"
              </p>
            ) : (
              filteredCodes.map((code) => (
                <StarCodeItem
                  key={code.id || code.code}
                  code={code}
                  onClick={() => handleCodeClick(code)}
                />
              ))
            )}
          </div>
        ) : (
          // Grouped by category
          <Accordion
            type="multiple"
            value={expandedCategories}
            onValueChange={setExpandedCategories}
            className="space-y-1"
          >
            {categories.map((category) => {
              const codes = codesByCategory[category] || [];
              if (codes.length === 0) return null;
              
              return (
                <AccordionItem
                  key={category}
                  value={category}
                  className="border-none"
                >
                  <AccordionTrigger className="py-2 px-3 hover:bg-accent/50 rounded-md hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {codes.length}
                      </Badge>
                      <span className="text-sm font-medium">{category}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-1 pb-2">
                    <div className="space-y-1 pl-2">
                      {codes.map((code) => (
                        <StarCodeItem
                          key={code.id || code.code}
                          code={code}
                          onClick={() => handleCodeClick(code)}
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>
      
      {/* Input Dialog for codes that need additional input */}
      <Dialog open={inputDialogOpen} onOpenChange={setInputDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hash className="w-5 h-5 text-cyan-500" />
              {selectedCode?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2 text-lg font-mono bg-muted/50 rounded-md px-3 py-2">
              <span className="text-cyan-500">{selectedCode?.code}</span>
              <Input
                placeholder={selectedCode?.input_placeholder || "Enter number"}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value.replace(/[^0-9*#]/g, ""))}
                className="border-0 bg-transparent text-lg font-mono p-0 h-auto focus-visible:ring-0"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleInputSubmit()}
                data-testid="star-code-input"
              />
            </div>
            {selectedCode?.description && (
              <p className="text-sm text-muted-foreground">
                {selectedCode.description}
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInputDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleInputSubmit}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="dial-star-code-btn"
            >
              <Phone className="w-4 h-4 mr-2" />
              Dial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Individual star code item
function StarCodeItem({ code, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-accent/70 transition-colors text-left group"
      data-testid={`star-code-${code.code.replace(/[*#]/g, "")}`}
    >
      <div className="w-14 font-mono text-sm text-cyan-500 font-medium">
        {code.code}
        {code.requires_input && <span className="text-muted-foreground">...</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{code.name}</div>
        {code.description && (
          <div className="text-xs text-muted-foreground truncate">
            {code.description}
          </div>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

export default StarCodesPanel;
